/**
 * CollisionSystem - Handles AABB collision detection and resolution
 * Uses spatial partitioning (grid) for efficient broad-phase collision detection
 */

import type { Vector2, AABB } from '../types/game';
import type { Entity, Collider, CollisionLayer } from '../types/entities';
import type { CollisionInfo, RaycastHit } from '../types/systems';
import { getPhysicsEngine } from './PhysicsEngine';
import { debugSettings } from '../utils/DebugSettings';

export class CollisionSystem {
  // Collision tracking
  private colliders: Map<string, Collider> = new Map();
  private entities: Map<string, Entity> = new Map();

  // Spatial grid for broad-phase collision
  private spatialGrid: SpatialGrid;
  private gridCellSize: number = 128; // pixels

  // Collision matrix (which layers collide with which)
  private collisionMatrix: Map<string, Set<CollisionLayer>> = new Map();

  // Callbacks
  private onCollisionEnter: ((entityA: string, entityB: string, info: CollisionInfo) => void) | null = null;
  private onTriggerEnter: ((entityA: string, entityB: string) => void) | null = null;

  // Performance tracking
  private enabled: boolean = true;
  private broadPhaseChecks: number = 0;
  private narrowPhaseChecks: number = 0;
  private collisionsDetected: number = 0;

  constructor(worldWidth: number = 2048, worldHeight: number = 2048, cellSize: number = 128) {
    this.gridCellSize = cellSize;
    this.spatialGrid = new SpatialGrid(worldWidth, worldHeight, cellSize);
    this.initializeCollisionMatrix();
    console.log('CollisionSystem: Initialized with grid size', worldWidth, 'x', worldHeight, 'cell size', cellSize);
  }

  /**
   * Initialize collision matrix (which layers collide with which)
   */
  private initializeCollisionMatrix(): void {
    // Player collides with: static, robot, trigger, projectile
    this.collisionMatrix.set('player', new Set(['static', 'robot', 'trigger', 'projectile']));

    // Robot collides with: static, player, trigger
    this.collisionMatrix.set('robot', new Set(['static', 'player', 'trigger']));

    // Static collides with: player, robot, projectile
    this.collisionMatrix.set('static', new Set(['player', 'robot', 'projectile']));

    // Trigger collides with: player, robot
    this.collisionMatrix.set('trigger', new Set(['player', 'robot']));

    // Projectile collides with: player, static
    this.collisionMatrix.set('projectile', new Set(['player', 'static']));
  }

  /**
   * Check if two layers should collide
   */
  private shouldCollide(layerA: CollisionLayer, layerB: CollisionLayer): boolean {
    const collisions = this.collisionMatrix.get(layerA);
    return collisions?.has(layerB) ?? false;
  }

  /**
   * Add a collider for an entity
   * IMPORTANT: Stores reference to the actual entity object, not a copy
   */
  public addCollider(entity: Entity, colliderConfig?: Partial<Collider>): void {
    // Store the actual entity reference so position updates are reflected
    this.entities.set(entity.id, entity);

    const bounds: AABB = {
      min: {
        x: entity.position.x - entity.size.width / 2,
        y: entity.position.y - entity.size.height / 2,
      },
      max: {
        x: entity.position.x + entity.size.width / 2,
        y: entity.position.y + entity.size.height / 2,
      },
    };

    const collider: Collider = {
      entityId: entity.id,
      type: colliderConfig?.type ?? 'box',
      bounds: bounds,
      isTrigger: colliderConfig?.isTrigger ?? false,
      layer: colliderConfig?.layer ?? 'static',
    };

    this.colliders.set(entity.id, collider);
    this.spatialGrid.insert(collider);
  }

  /**
   * Remove a collider
   */
  public removeCollider(entityId: string): void {
    const collider = this.colliders.get(entityId);
    if (collider) {
      this.spatialGrid.remove(collider);
      this.colliders.delete(entityId);
    }
    this.entities.delete(entityId);
  }

  /**
   * Update collider bounds based on entity position
   */
  public updateCollider(entityId: string): void {
    const entity = this.entities.get(entityId);
    const collider = this.colliders.get(entityId);

    if (!entity || !collider) {
      return;
    }

    // Remove from grid
    this.spatialGrid.remove(collider);

    // Update bounds
    collider.bounds.min.x = entity.position.x - entity.size.width / 2;
    collider.bounds.min.y = entity.position.y - entity.size.height / 2;
    collider.bounds.max.x = entity.position.x + entity.size.width / 2;
    collider.bounds.max.y = entity.position.y + entity.size.height / 2;

    // Re-insert into grid
    this.spatialGrid.insert(collider);
  }

  /**
   * Update collision system - check all collisions
   */
  public update(): void {
    if (!this.enabled) {
      return;
    }

    // Reset performance counters
    this.broadPhaseChecks = 0;
    this.narrowPhaseChecks = 0;
    this.collisionsDetected = 0;

    const physicsEngine = getPhysicsEngine();

    // First, assume all dynamic entities are not grounded (will be set to true if collision detected)
    for (const [entityId, collider] of this.colliders) {
      if (collider.layer === 'static') {
        continue;
      }
      physicsEngine.setGrounded(entityId, false);
    }

    // Update all collider positions
    for (const entityId of this.entities.keys()) {
      this.updateCollider(entityId);
    }

    // Check collisions for all dynamic entities
    for (const [entityId, collider] of this.colliders) {
      if (collider.layer === 'static') {
        continue; // Static objects don't check collisions (others check against them)
      }

      this.checkCollisionsForEntity(entityId);
    }
  }

  /**
   * Check collisions for a specific entity
   */
  private checkCollisionsForEntity(entityId: string): void {
    const collider = this.colliders.get(entityId);
    if (!collider) {
      return;
    }

    // Broad phase: Get potential collisions from spatial grid
    const potentialCollisions = this.spatialGrid.query(collider.bounds);
    this.broadPhaseChecks += potentialCollisions.length;

    // Narrow phase: Check actual collisions
    for (const otherCollider of potentialCollisions) {
      // Skip self
      if (otherCollider.entityId === entityId) {
        continue;
      }

      // Check collision matrix
      if (!this.shouldCollide(collider.layer, otherCollider.layer)) {
        continue;
      }

      this.narrowPhaseChecks++;

      // Check collision
      const collision = this.checkAABBCollision(collider, otherCollider);
      if (collision) {
        this.collisionsDetected++;

        // Handle trigger vs solid collision
        if (collider.isTrigger || otherCollider.isTrigger) {
          // Trigger collision
          if (this.onTriggerEnter) {
            this.onTriggerEnter(collider.entityId, otherCollider.entityId);
          }
        } else {
          // Solid collision - resolve it
          this.resolveCollision(collision);

          if (this.onCollisionEnter) {
            this.onCollisionEnter(collider.entityId, otherCollider.entityId, collision);
          }
        }
      }
    }
  }

  /**
   * AABB collision detection
   */
  private checkAABBCollision(a: Collider, b: Collider): CollisionInfo | null {
    const aabb1 = a.bounds;
    const aabb2 = b.bounds;

    // Check if AABBs overlap
    if (
      aabb1.max.x < aabb2.min.x ||
      aabb1.min.x > aabb2.max.x ||
      aabb1.max.y < aabb2.min.y ||
      aabb1.min.y > aabb2.max.y
    ) {
      return null; // No collision
    }

    // Calculate penetration depth for each axis
    const overlapX = Math.min(aabb1.max.x - aabb2.min.x, aabb2.max.x - aabb1.min.x);
    const overlapY = Math.min(aabb1.max.y - aabb2.min.y, aabb2.max.y - aabb1.min.y);

    // Calculate center positions to determine direction
    const centerA = {
      x: (aabb1.min.x + aabb1.max.x) / 2,
      y: (aabb1.min.y + aabb1.max.y) / 2,
    };
    const centerB = {
      x: (aabb2.min.x + aabb2.max.x) / 2,
      y: (aabb2.min.y + aabb2.max.y) / 2,
    };

    // Find the axis of least penetration
    let normal: Vector2;
    let penetration: number;

    if (overlapX < overlapY) {
      // Separate on X axis
      penetration = overlapX;
      // Normal points from B to A
      normal = {
        x: centerA.x > centerB.x ? 1 : -1,
        y: 0,
      };
    } else {
      // Separate on Y axis
      penetration = overlapY;
      // Normal points from B to A
      normal = {
        x: 0,
        y: centerA.y > centerB.y ? 1 : -1,
      };
    }

    // Calculate collision point (center of overlap)
    const point: Vector2 = {
      x: (Math.max(aabb1.min.x, aabb2.min.x) + Math.min(aabb1.max.x, aabb2.max.x)) / 2,
      y: (Math.max(aabb1.min.y, aabb2.min.y) + Math.min(aabb1.max.y, aabb2.max.y)) / 2,
    };

    return {
      colliderA: a,
      colliderB: b,
      normal: normal,
      penetration: penetration,
      point: point,
    };
  }

  /**
   * Resolve collision - separate entities and update physics
   */
  private resolveCollision(collision: CollisionInfo): void {
    const entityA = this.entities.get(collision.colliderA.entityId);
    const entityB = this.entities.get(collision.colliderB.entityId);

    if (!entityA || !entityB) {
      return;
    }

    const physicsEngine = getPhysicsEngine();

    // Determine which entity to move
    const isAStatic = collision.colliderA.layer === 'static';
    const isBStatic = collision.colliderB.layer === 'static';
    const isARobot = collision.colliderA.layer === 'robot';
    const isBRobot = collision.colliderB.layer === 'robot';

    if (isAStatic && isBStatic) {
      return; // Both static, no resolution needed
    }

    // Noclip: skip all solid collisions involving the player
    const isAPlayer = collision.colliderA.layer === 'player';
    const isBPlayer = collision.colliderB.layer === 'player';
    if (debugSettings.noclip && (isAPlayer || isBPlayer)) {
      return;
    }

    // Robots are immovable (like static objects for collision purposes)
    // Only the player gets pushed by robots, not the other way around
    if (isARobot && isBRobot) {
      return; // Both robots, no resolution needed
    }

    // Separate entities along collision normal
    const separation = {
      x: collision.normal.x * collision.penetration,
      y: collision.normal.y * collision.penetration,
    };

    if (isBStatic || isBRobot) {
      // Only move A (separate from static platform or robot)
      entityA.position.x += separation.x;
      entityA.position.y += separation.y;

      // Handle vertical collisions (platforms and ceilings)
      if (collision.normal.y > 0) {
        // A is on top of B (landing on platform from above or standing on it)
        // Set grounded regardless of velocity to handle standing still
        physicsEngine.setGrounded(entityA.id, true);

        // Only stop downward velocity, preserve upward velocity if jumping
        if (entityA.velocity.y < 0) {
          entityA.velocity.y = 0; // Stop falling
        }

        // Snap to exact platform surface to prevent jitter and tunneling
        const platformTop = entityB.position.y + entityB.size.height / 2;
        entityA.position.y = platformTop + entityA.size.height / 2;
      } else if (collision.normal.y < 0) {
        // A is hitting B from below (jumping up into ceiling)
        physicsEngine.setGrounded(entityA.id, false);

        // Only stop upward velocity
        if (entityA.velocity.y > 0) {
          entityA.velocity.y = 0; // Stop upward movement
        }
      }

      // Handle horizontal collisions (walls)
      if (collision.normal.x !== 0) {
        entityA.velocity.x = 0; // Stop horizontal movement
      }
    } else if (isAStatic || isARobot) {
      // Only move B (robot or static object doesn't move)
      entityB.position.x -= separation.x;
      entityB.position.y -= separation.y;

      // Handle platform landing
      if (collision.normal.y < 0) {
        physicsEngine.setGrounded(entityB.id, true);
        entityB.velocity.y = 0;
      }

      // Handle ceiling collision
      if (collision.normal.y > 0) {
        entityB.velocity.y = 0;
      }

      // Handle wall collision
      if (collision.normal.x !== 0) {
        entityB.velocity.x = 0;
      }
    } else {
      // Both dynamic - split separation
      entityA.position.x += separation.x * 0.5;
      entityA.position.y += separation.y * 0.5;
      entityB.position.x -= separation.x * 0.5;
      entityB.position.y -= separation.y * 0.5;

      // Apply some velocity dampening
      entityA.velocity.x *= 0.8;
      entityA.velocity.y *= 0.8;
      entityB.velocity.x *= 0.8;
      entityB.velocity.y *= 0.8;
    }
  }

  /**
   * Raycast - cast a ray and return the first hit
   */
  public raycast(origin: Vector2, direction: Vector2, maxDistance: number, layerMask?: CollisionLayer[]): RaycastHit | null {
    // Normalize direction
    const length = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
    const normalizedDir = {
      x: direction.x / length,
      y: direction.y / length,
    };

    // Create ray bounds for broad phase
    const endPoint = {
      x: origin.x + normalizedDir.x * maxDistance,
      y: origin.y + normalizedDir.y * maxDistance,
    };

    const rayBounds: AABB = {
      min: {
        x: Math.min(origin.x, endPoint.x),
        y: Math.min(origin.y, endPoint.y),
      },
      max: {
        x: Math.max(origin.x, endPoint.x),
        y: Math.max(origin.y, endPoint.y),
      },
    };

    // Query spatial grid
    const potentialHits = this.spatialGrid.query(rayBounds);

    let closestHit: RaycastHit | null = null;
    let closestDistance = maxDistance;

    for (const collider of potentialHits) {
      // Check layer mask
      if (layerMask && !layerMask.includes(collider.layer)) {
        continue;
      }

      // Ray-AABB intersection test
      const hit = this.rayAABBIntersection(origin, normalizedDir, closestDistance, collider);
      if (hit && hit.distance < closestDistance) {
        closestHit = hit;
        closestDistance = hit.distance;
      }
    }

    return closestHit;
  }

  /**
   * Ray-AABB intersection test
   */
  private rayAABBIntersection(origin: Vector2, direction: Vector2, maxDistance: number, collider: Collider): RaycastHit | null {
    const bounds = collider.bounds;

    // Calculate intersection distances
    const tMin = {
      x: (bounds.min.x - origin.x) / direction.x,
      y: (bounds.min.y - origin.y) / direction.y,
    };

    const tMax = {
      x: (bounds.max.x - origin.x) / direction.x,
      y: (bounds.max.y - origin.y) / direction.y,
    };

    // Swap if needed
    if (tMin.x > tMax.x) {
      [tMin.x, tMax.x] = [tMax.x, tMin.x];
    }
    if (tMin.y > tMax.y) {
      [tMin.y, tMax.y] = [tMax.y, tMin.y];
    }

    // Check for intersection
    const tEnter = Math.max(tMin.x, tMin.y);
    const tExit = Math.min(tMax.x, tMax.y);

    if (tEnter > tExit || tExit < 0 || tEnter > maxDistance) {
      return null; // No intersection
    }

    // Calculate hit point and normal
    const distance = tEnter;
    const point = {
      x: origin.x + direction.x * distance,
      y: origin.y + direction.y * distance,
    };

    // Determine normal based on which face was hit
    let normal: Vector2;
    if (Math.abs(tMin.x - tEnter) < 0.001) {
      normal = { x: direction.x > 0 ? -1 : 1, y: 0 };
    } else {
      normal = { x: 0, y: direction.y > 0 ? -1 : 1 };
    }

    return {
      collider: collider,
      point: point,
      normal: normal,
      distance: distance,
    };
  }

  /**
   * Set collision callback
   */
  public setCollisionCallback(callback: (entityA: string, entityB: string, info: CollisionInfo) => void): void {
    this.onCollisionEnter = callback;
  }

  /**
   * Set trigger callback
   */
  public setTriggerCallback(callback: (entityA: string, entityB: string) => void): void {
    this.onTriggerEnter = callback;
  }

  /**
   * Enable/disable collision system
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Clear all colliders
   */
  public clear(): void {
    this.colliders.clear();
    this.entities.clear();
    this.spatialGrid.clear();
  }

  /**
   * Get performance stats
   */
  public getStats(): CollisionStats {
    return {
      colliderCount: this.colliders.size,
      broadPhaseChecks: this.broadPhaseChecks,
      narrowPhaseChecks: this.narrowPhaseChecks,
      collisionsDetected: this.collisionsDetected,
      enabled: this.enabled,
    };
  }
}

/**
 * Spatial Grid for broad-phase collision detection
 * Supports negative coordinates by centering the grid at (0, 0)
 */
class SpatialGrid {
  private cellSize: number;
  private width: number;
  private height: number;
  private offsetX: number; // Offset to support negative coordinates
  private offsetY: number;
  private cells: Map<string, Collider[]> = new Map();

  constructor(width: number, height: number, cellSize: number) {
    this.width = width;
    this.height = height;
    this.cellSize = cellSize;
    // Center the grid at (0, 0) by offsetting by half the world size
    this.offsetX = width / 2;
    this.offsetY = height / 2;
  }

  /**
   * Get cell key from world position
   */
  private getCellKey(x: number, y: number): string {
    const col = Math.floor((x + this.offsetX) / this.cellSize);
    const row = Math.floor((y + this.offsetY) / this.cellSize);
    return `${col},${row}`;
  }

  /**
   * Insert collider into grid
   */
  public insert(collider: Collider): void {
    const bounds = collider.bounds;

    // Convert world coordinates to grid coordinates
    const minCol = Math.floor((bounds.min.x + this.offsetX) / this.cellSize);
    const maxCol = Math.floor((bounds.max.x + this.offsetX) / this.cellSize);
    const minRow = Math.floor((bounds.min.y + this.offsetY) / this.cellSize);
    const maxRow = Math.floor((bounds.max.y + this.offsetY) / this.cellSize);

    // Add to all overlapping cells
    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const key = `${col},${row}`;
        if (!this.cells.has(key)) {
          this.cells.set(key, []);
        }
        this.cells.get(key)!.push(collider);
      }
    }
  }

  /**
   * Remove collider from grid
   */
  public remove(collider: Collider): void {
    const bounds = collider.bounds;

    const minCol = Math.floor((bounds.min.x + this.offsetX) / this.cellSize);
    const maxCol = Math.floor((bounds.max.x + this.offsetX) / this.cellSize);
    const minRow = Math.floor((bounds.min.y + this.offsetY) / this.cellSize);
    const maxRow = Math.floor((bounds.max.y + this.offsetY) / this.cellSize);

    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const key = `${col},${row}`;
        const cell = this.cells.get(key);
        if (cell) {
          const index = cell.indexOf(collider);
          if (index !== -1) {
            cell.splice(index, 1);
          }
        }
      }
    }
  }

  /**
   * Query colliders in bounds
   */
  public query(bounds: AABB): Collider[] {
    const minCol = Math.floor((bounds.min.x + this.offsetX) / this.cellSize);
    const maxCol = Math.floor((bounds.max.x + this.offsetX) / this.cellSize);
    const minRow = Math.floor((bounds.min.y + this.offsetY) / this.cellSize);
    const maxRow = Math.floor((bounds.max.y + this.offsetY) / this.cellSize);

    const result: Set<Collider> = new Set();

    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const key = `${col},${row}`;
        const cell = this.cells.get(key);
        if (cell) {
          for (const collider of cell) {
            result.add(collider);
          }
        }
      }
    }

    return Array.from(result);
  }

  /**
   * Clear grid
   */
  public clear(): void {
    this.cells.clear();
  }
}

export interface CollisionStats {
  colliderCount: number;
  broadPhaseChecks: number;
  narrowPhaseChecks: number;
  collisionsDetected: number;
  enabled: boolean;
}

// Singleton instance
let collisionSystemInstance: CollisionSystem | null = null;

/**
 * Get the singleton CollisionSystem instance
 */
export function getCollisionSystem(): CollisionSystem {
  if (!collisionSystemInstance) {
    collisionSystemInstance = new CollisionSystem();
  }
  return collisionSystemInstance;
}
